"use client";

import { AudioProvider, useAudio } from "@/app/_providers/AudioProvider";

import { Disc, DiscImageConfig } from "@ui/components/core/Disc/Disc";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import { cva, type VariantProps } from "class-variance-authority";

const containerVariants = cva([], {
  variants: {
    size: {
      default: "tw-h-32 tw-w-32 md:tw-h-40 md:tw-w-40 lg:tw-h-32 lg:tw-w-32 xl:tw-h-40 xl:tw-w-40",
      lg: "tw-h-36 tw-w-36 sm:tw-h-44 sm:tw-w-44 md:tw-h-52 md:tw-w-52",
      "responsive-sm": "tw-size-24 sm:tw-size-36 lg:tw-size-52",
      "responsive-md": "tw-size-24 sm:tw-size-36 md:tw-size-52",
      "responsive-full": "tw-w-full tw-max-w-44 tw-aspect-square",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const dividerVariants = cva("tw-relative", {
  variants: {
    size: {
      default: "tw-h-32 sm:tw-h-40 md:tw-h-52 lg:tw-h-40 xl:tw-h-52",
      "responsive-sm": "tw-h-36 sm:tw-h-44 md:tw-h-52",
      "responsive-md": "tw-h-36 sm:tw-h-44 md:tw-h-52",
      lg: "tw-h-36 sm:tw-h-44 md:tw-h-52",
      "responsive-full": "tw-h-32 sm:tw-h-40 md:tw-h-44",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

export interface SideBySideLayoutProps extends VariantProps<typeof containerVariants> {
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  leftLabel: string;
  rightLabel: string;
  className?: string;
  showCenterDivider?: boolean;
}

/**
 * Reusable layout component for side-by-side content with labels and optional divider.
 * Can be used standalone or composed with other components.
 */
export function SideBySideLayout({
  leftContent,
  rightContent,
  leftLabel,
  rightLabel,
  className,
  showCenterDivider = true,
  size,
}: SideBySideLayoutProps) {
  return (
    <div className={className}>
      <div className='tw-grid tw-grid-cols-[1fr_auto_1fr] tw-items-center tw-gap-4 sm:tw-gap-6 md:tw-gap-10 lg:tw-gap-6 xl:tw-gap-10'>
        <div className='tw-flex tw-flex-col tw-items-center tw-gap-2 sm:tw-gap-3 md:tw-gap-5'>
          <div className={containerVariants({ size })}>{leftContent}</div>
          <p className='tw-h-[2lh] tw-line-clamp-2 tw-text-center tw-text-sm tw-font-normal tw-leading-[1.2] tw-text-black tw-opacity-60'>
            {leftLabel}
          </p>
        </div>

        <div className={dividerVariants({ size })}>
          {showCenterDivider && <DottedBorder side='vertical' color='light' />}
        </div>

        <div className='tw-flex tw-flex-col tw-items-center tw-gap-2 sm:tw-gap-3 md:tw-gap-5'>
          <div className={containerVariants({ size })}>{rightContent}</div>
          <p className='tw-h-[2lh] tw-line-clamp-2 tw-text-center tw-text-sm tw-font-normal tw-leading-[1.2] tw-text-black tw-opacity-60'>
            {rightLabel}
          </p>
        </div>
      </div>
    </div>
  );
}

export interface SideBySideDiscConfig {
  label: string;
  image?: DiscImageConfig;
  audioUrl: string;
}

export interface SideBySideDiscsProps extends VariantProps<typeof containerVariants> {
  leftDisc: SideBySideDiscConfig;
  rightDisc: SideBySideDiscConfig;
  className?: string;
  showCenterDivider?: boolean;
  /** Source identifier for tracking (e.g., "voice_cloning_demo", "voice_changer_demo") */
  trackingSource?: string;
}

/**
 * Core component that requires an AudioProvider context.
 * Use when composing multiple audio components within a shared AudioProvider.
 */
export function SideBySideDiscsContent({
  leftDisc,
  rightDisc,
  className,
  showCenterDivider = true,
  trackingSource,
  size,
}: SideBySideDiscsProps) {
  const { play, playingId, stop } = useAudio();

  const handlePlayPauseClick = (disc: "left" | "right") => {
    const discConfig = disc === "left" ? leftDisc : rightDisc;

    const isPlaying = playingId === discConfig.audioUrl;

    if (isPlaying) {
      stop();
    } else {
      play(discConfig.audioUrl, discConfig.audioUrl, false, {
        source: trackingSource,
        disc_position: disc,
      });
    }
  };

  return (
    <SideBySideLayout
      className={className}
      showCenterDivider={showCenterDivider}
      size={size}
      leftLabel={leftDisc.label}
      rightLabel={rightDisc.label}
      leftContent={
        <Disc
          image={leftDisc.image}
          isPlaying={playingId === leftDisc.audioUrl}
          onPlayPauseClick={() => handlePlayPauseClick("left")}
        />
      }
      rightContent={
        <Disc
          image={rightDisc.image}
          isPlaying={playingId === rightDisc.audioUrl}
          onPlayPauseClick={() => handlePlayPauseClick("right")}
        />
      }
    />
  );
}

/**
 * Convenience wrapper with AudioProvider included for standalone use.
 * For coordinated audio playback, use SideBySideDiscsContent within a shared AudioProvider.
 */
export function SideBySideDiscs({ trackingSource, size, ...props }: SideBySideDiscsProps) {
  return (
    <AudioProvider>
      <SideBySideDiscsContent {...props} size={size} trackingSource={trackingSource} />
    </AudioProvider>
  );
}
